import { Button, Typography } from '@capsule/consumer-design-system';

import { useTranslations } from 'next-intl';
import { css } from 'styled-components';

import LinkButton from 'src/app/components/Elements/Button/LinkButton';
import type { PharmacyHubEntryResponse } from 'src/app/hooks/data/pharmacyHub';
import { useIsSpanishRoute } from 'src/app/hooks/useIsSpanishRoute';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { clearRxHIPAA, clearRxPrivacy, clearRxTerms } from 'src/app/router/paths';

import { ClearRxLayout } from '../ClearRxLayout';
import { LANGUAGES_SUPPORTED } from '../constants';
import { Capitalize, Lowercase, StyledMedCard } from '../styles';

import { StyledMainPageContent } from './styles';

type PharmacyHubWelcomeProps = {
  handleStartConfirmPharmacyClick: () => void;
  pendingEscripts: PharmacyHubEntryResponse;
};

export const PharmacyHubWelcome = (props: PharmacyHubWelcomeProps) => {
  const { handleStartConfirmPharmacyClick, pendingEscripts } = props;
  const t = useTranslations('pharmacySelection.welcome');
  const isSpanishRoute = useIsSpanishRoute();
  const numPendingEscripts = pendingEscripts.medications.length;
  const medicationNamesList = pendingEscripts.medications.map(i => i.primary_name).join(', ');

  useSegmentEvent('viewClearRxPrescriptions', {
    shouldTrackOnMount: true,
    payload: {
      referral: typeof window !== 'undefined' ? document.referrer : null,
      medication_count: numPendingEscripts,
      medication_name: medicationNamesList,
      provider_npi: pendingEscripts.provider.npi,
      provider_name: `${pendingEscripts.provider.first_name} ${pendingEscripts.provider.last_name}`,
      practice_name: pendingEscripts.provider.practice_name ?? '',
      emr_department_name: pendingEscripts.department?.name || '',
      emr_department_id: pendingEscripts.department?.emr_department_id || '',
      language_used: isSpanishRoute ? LANGUAGES_SUPPORTED.SPANISH : LANGUAGES_SUPPORTED.ENGLISH,
    },
  });

  const providerName = `${pendingEscripts.provider.first_name} ${pendingEscripts.provider.last_name}`;

  return (
    <StyledMainPageContent>
      <ClearRxLayout>
        <Typography
          variant="heading1"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s4};
          `}
        >
          {t('title', { name: pendingEscripts.patient.first_name })}
        </Typography>
        <Typography
          variant="body"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s6};
          `}
        >
          {t('subtitle', { provider: providerName })}
        </Typography>
        {pendingEscripts.medications.map(med => (
          <StyledMedCard>
            <Typography variant={'heading3'}>
              <Capitalize>{med.primary_name}</Capitalize> {med.strength}
            </Typography>
            {med.dosage_form_label && (
              <Typography variant={'body'}>
                <Lowercase>
                  {med.quantity} {med.dosage_form_label}
                </Lowercase>
              </Typography>
            )}
            {med.num_refills_remaining > 0 && (
              <Typography
                variant={'small'}
                css={css`
                  color: ${({ theme }) => theme.colors.capsuleGreen60};
                `}
              >
                {med.num_refills_remaining > 1
                  ? t('refillsText', { count: med.num_refills_remaining })
                  : t('refillText', { count: med.num_refills_remaining })}
              </Typography>
            )}
          </StyledMedCard>
        ))}
        <Button
          onClick={handleStartConfirmPharmacyClick}
          isFullWidth
          variant="contained-secondary-lg"
          css={css`
            margin-top: ${({ theme }) => theme.space.s7};
          `}
        >
          {t('continueButton')}
        </Button>
        <Typography
          variant="body"
          css={css`
            margin-top: ${({ theme }) => theme.space.s4};
          `}
        >
          {t('agreementIntro')}{' '}
          <LinkButton style="inlineLink" to={clearRxTerms.toPath()} rel="noopener noreferrer" target="_blank">
            {t('termsText')}
          </LinkButton>
          ,{' '}
          <LinkButton style="inlineLink" to={clearRxPrivacy.toPath()} rel="noopener noreferrer" target="_blank">
            {t('privacyText')}
          </LinkButton>
          , and{' '}
          <LinkButton style="inlineLink" to={clearRxHIPAA.toPath()} rel="noopener noreferrer" target="_blank">
            {t('hipaaText')}
          </LinkButton>
          .
        </Typography>
      </ClearRxLayout>
    </StyledMainPageContent>
  );
};
